import TextField from "@material-ui/core/TextField";
import React from "react";
import styled, { keyframes } from "styled-components";
import { Settings, Schedule, Info } from "@material-ui/icons";

function TabSettings({ values, touched, errors, handleChange, formik }) {
  const getRateStatus = (rate) => {
    if (rate < 30) return { text: 'Valor muito baixo (mínimo 30 minutos)', color: '#e74c3c' };
    if (rate >= 30 && rate <= 60) return { text: 'Execução frequente', color: '#27ae60' };
    if (rate > 60 && rate <= 120) return { text: 'Execução moderada', color: '#f39c12' };
    return { text: 'Execução espaçada', color: '#3498db' };
  };

  const rateStatus = getRateStatus(values.rate);

  return (
    <ModernFormContainer>
      <SectionCard>
        <SectionHeader>
          <SectionIcon color="#667eea">
            <Settings />
          </SectionIcon>
          <SectionTitle>Configurações de Execução</SectionTitle>
        </SectionHeader>

        <InfoBanner>
          <Info style={{ fontSize: '20px', marginRight: '8px' }} />
          Configure o intervalo de tempo entre as execuções automáticas
        </InfoBanner>

        <FormContent>
          <StyledFieldContainer>
            <FieldLabel>
              <Schedule style={{ fontSize: '18px', marginRight: '8px', color: '#667eea' }} />
              Rate de Execução (em minutos)
            </FieldLabel>
            <TextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              id="rate"
              name="rate"
              placeholder="Ex: 30"
              type="number"
              value={values.rate}
              onChange={handleChange}
              error={touched.rate && Boolean(errors.rate)}
              helperText={touched.rate && errors.rate}
              autoComplete="off"
              variant="outlined"
            />
          </StyledFieldContainer>

          <RateInfo>
            <RateStatusCard>
              <RateStatusLabel>Status Atual</RateStatusLabel>
              <RateStatusValue color={rateStatus.color}>
                {rateStatus.text}
              </RateStatusValue>
            </RateStatusCard>

            <RateMetrics>
              <MetricCard>
                <MetricIcon>
                  <Schedule />
                </MetricIcon>
                <MetricContent>
                  <MetricLabel>Execuções por Hora</MetricLabel>
                  <MetricValue>
                    {values.rate > 0 ? Math.floor(60 / values.rate) : 0}
                  </MetricValue>
                </MetricContent>
              </MetricCard>

              <MetricCard>
                <MetricIcon>
                  <Schedule />
                </MetricIcon>
                <MetricContent>
                  <MetricLabel>Execuções por Dia</MetricLabel>
                  <MetricValue>
                    {values.rate > 0 ? Math.floor(1440 / values.rate) : 0}
                  </MetricValue>
                </MetricContent>
              </MetricCard>
            </RateMetrics>
          </RateInfo>

          <WarningBox show={values.rate < 30}>
            <Info style={{ fontSize: '20px', marginRight: '8px' }} />
            <WarningText>
              <strong>Atenção:</strong> O valor mínimo permitido é de 30 minutos para evitar sobrecarga no sistema.
            </WarningText>
          </WarningBox>
        </FormContent>
      </SectionCard>
    </ModernFormContainer>
  );
}

export default TabSettings;

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ModernFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Mulish', sans-serif;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid #e8e8e8;
  animation: ${fadeIn} 0.5s ease;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #d0d0d0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const SectionIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color ? `${props.color}15` : '#667eea15'};
  color: ${props => props.color || '#667eea'};
  margin-right: 12px;
  transition: all 0.3s ease;

  svg {
    font-size: 22px;
  }

  ${SectionCard}:hover & {
    transform: scale(1.1);
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  font-family: 'Mulish', sans-serif;
`;

const InfoBanner = styled.div`
  background: rgba(52, 152, 219, 0.1);
  border-left: 4px solid #3498db;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  font-family: 'Mulish', sans-serif;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledFieldContainer = styled.div`
  display: flex;
  flex-direction: column;

  .MuiOutlinedInput-root {
    border-radius: 10px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
    }

    &.Mui-focused {
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    }
  }

  .MuiInputLabel-outlined {
    font-family: 'Mulish', sans-serif;
    font-weight: 500;
  }

  .MuiOutlinedInput-input {
    font-family: 'Mulish', sans-serif;
  }
`;

const FieldLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;
`;

const RateInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RateStatusCard = styled.div`
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
`;

const RateStatusLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #95a5a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  font-family: 'Mulish', sans-serif;
`;

const RateStatusValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.color || '#667eea'};
  font-family: 'Mulish', sans-serif;
`;

const RateMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: #f8f9fa;
  padding: 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(102, 126, 234, 0.05);
    transform: translateY(-2px);
  }
`;

const MetricIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;

  svg {
    font-size: 22px;
  }
`;

const MetricContent = styled.div`
  flex: 1;
`;

const MetricLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #95a5a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-family: 'Mulish', sans-serif;
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  font-family: 'Mulish', sans-serif;
`;

const WarningBox = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border-left: 4px solid #e74c3c;
  padding: 12px 16px;
  border-radius: 8px;
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  color: #e74c3c;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
`;

const WarningText = styled.div`
  flex: 1;
  line-height: 1.5;
`;
